import { ref, computed, onMounted, watch } from 'vue';
import HiCache from 'utils/cache';
import { usePWA } from './usePWA';

export default function usePwaClient() {
    // const isPwaMode = ref(false);

    // 新增一个标志，表示是否从卸载状态中恢复
    const isRecoveredFromUninstall = ref(false);

    // 获取url参数
    function getUrlParam(name: string, url?: string): string | null {
        // 如果提供了完整URL，则使用它，否则使用当前页面URL
        const urlString = url || window.location.search;

        // 处理完整URL情况 (如 http://localhost:6002/?sdmode=2)
        let searchParams: URLSearchParams;
        if (url && url.includes('?')) {
            // 提取URL中的查询参数部分
            const queryString = url.split('?')[1];
            searchParams = new URLSearchParams(queryString);
        } else {
            // 使用当前页面URL或直接使用传入的查询字符串
            searchParams = new URLSearchParams(urlString);
        }

        return searchParams.get(name);
    }

    // 获取所有URL参数
    function getUrlParams() {
        const params = {};
        // 直接从URL的search参数中获取
        const searchStr = window.location.search.substring(1); // 移除开头的 '?'

        if (searchStr) {
            const searchParams = new URLSearchParams(searchStr);
            for (const [key, value] of searchParams.entries()) {
                params[key] = value;
            }
        }
        return params;
    }

    // 从Cookie中获取指定名称的值
    function getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) {
            return match[2];
        }
        return null;
    }

    const google = computed(() => {
        const localGoogle = localStorage.getItem('google');
        if (localGoogle) {
            return true;
        }
        return false;
    });

    const webApp = computed(() => {
        const webAppParam = getUrlParam('web_app');
        const webAppParam2 = getUrlParam('sdmode');
        if (webAppParam) {
            HiCache.setCache('web_app', '1');
            return true;
        }
        // 如果sdmode=3，也视为webapp模式
        if (webAppParam2 === '3') {
            HiCache.setCache('web_app', '1');
            return true;
        }
        return HiCache.getCache('web_app');
    });

    // 检查是否安装了PWA
    const isPwaInstalled = computed(() => {
        // 如果window.deferredPrompt存在 或者 localstorage里面存在pwa_install_time
        if (window.deferredPrompt && localStorage.getItem('pwa_install_time')) {
            return true;
        }
        // 默认返回false
        return false;
    });

    const isPwaMode = computed(() => {
        const sdmode = localStorage.getItem('sdmode');
        if (!window.deferredPrompt && sdmode === '2') {
            return true;
        }
        return false;
    });

    const isGoogleMode = computed(() => {
        const google = localStorage.getItem('google');
        if (google) {
            return true;
        }
        return false;
    });

    /**
     * web 网页模式
     * pwa PWA模式
     * app 内部马甲包模式
     * google 谷歌马甲包模式
     */
    const getClientType = (): string => {
        if (isGoogleMode.value) {
            return 'google';
        } else if (webApp.value) {
            return 'app';
        } else if (isPwaMode.value) {
            return 'pwa';
        } else {
            return 'web';
        }
    };

    let _logger = '';

    // 处理路由参数以及App套壳的参数
    function handleRouteAndWebviewParams() {
        if (webApp.value) {
            // 表示在apk环境下
            const appBindData = localStorage.getItem('app_bindData');
            let appBindDataObj: Record<string, any> = {};

            // 处理app_bindData
            if (appBindData) {
                // 判断是对象还是字符串
                if (typeof appBindData === 'object') {
                    appBindDataObj = appBindData as Record<string, any>;
                } else {
                    try {
                        appBindDataObj = JSON.parse(appBindData);
                    } catch (error) {
                        _logger += `解析app_bindData失败: ${(error as Error).message}, \n`;
                        appBindDataObj = {};
                    }
                }
            }

            // 获取URL参数，用于在appBindDataObj不存在或不完整时补充
            const urlParams = getUrlParams() as Record<string, any>;

            // 优先从appBindDataObj获取，其次从URL获取
            let code = appBindDataObj.code || urlParams.code;
            const fb_pixel_id = appBindDataObj.fb_pixel_id || appBindDataObj.fbPixelId || urlParams.fb_pixel_id || urlParams.fbPixelId;
            const domain = appBindDataObj.domain || urlParams.domain;
            const fbclid = appBindDataObj.fbclid || urlParams.fbclid;

            // 处理fb_pixel_id：优先使用已获取的数据，其次尝试从Cookie获取
            let pixelId = fb_pixel_id;
            if (!pixelId) {
                pixelId = getCookie('pixelId') || getCookie('fb_pixel_id');
                if (pixelId) {
                    _logger += `apk环境 app_fb_pixel_id 从Cookie获取: ${pixelId}, \n`;
                }
            }

            // 如果code=bank 就删除
            if (code === 'bank' || code === 'wattle') {
                code = '';
                HiCache.deleteCache('app_code');
            }

            if (code) {
                HiCache.setCache('app_code', code);
                _logger += `apk环境 app_code 已记录: ${code} , \n`;
            }

            // 保存pixelId到缓存
            if (pixelId) {
                HiCache.setCache('app_fb_pixel_id', pixelId);
                _logger += `apk环境 app_fb_pixel_id 已记录: ${pixelId}, \n`;
            }

            if (domain) {
                HiCache.setCache('app_domain', domain);
                _logger += `apk环境 app_domain 已记录: ${domain}, \n `;
            }
            if (fbclid) {
                HiCache.setCache('app_fbclid', fbclid);
                _logger += `apk环境 app_fbclid 已记录: ${fbclid}, \n`;
            }

            const registerInfo = `设备名称: 安卓apk ---- 日志信息: ${_logger}`;
            HiCache.setCache('registerInfo', registerInfo);
            return;
        }

        // 表示在h5环境下 从url中获取参数
        const urlParams = getUrlParams();
        const { code, fb_pixel_id, fbclid, fbPixelId } = urlParams as any;

        // 处理fb_pixel_id：优先使用URL参数，其次尝试从Cookie获取
        let pixelId = fb_pixel_id || fbPixelId;
        if (!pixelId) {
            pixelId = getCookie('pixelId') || getCookie('fb_pixel_id');
            if (pixelId) {
                _logger += `H5环境 app_fb_pixel_id 从Cookie获取: ${pixelId}, \n`;
            }
        }

        if (code) {
            HiCache.setCache('app_code', code);
            _logger += `H5环境 app_code 已记录: ${code}, \n`;
        }

        // 保存pixelId到缓存
        if (pixelId) {
            HiCache.setCache('app_fb_pixel_id', pixelId);
            _logger += `H5环境 app_fb_pixel_id 已记录: ${pixelId}, \n`;
        }

        if (fbclid) {
            HiCache.setCache('app_fbclid', fbclid);
            _logger += `H5环境 app_fbclid 已记录: ${fbclid}, \n`;
        }

        const registerInfo = `设备名称: H5或PWA ---- 日志信息: ${_logger}`;
        HiCache.setCache('registerInfo', registerInfo);
    }

    // PWA 更新提示相关
    const isShowPwaUpdate = ref(false);

    // 设置PWA更新提示监听
    function setupPwaUpdateWatch(userinfo) {
        watch(
            [() => webApp.value, () => isPwaMode.value, () => userinfo.value],
            ([webAppVal, isPwaModeVal, userinfoVal]) => {
                // 只有当需要显示时才更新，避免覆盖手动关闭的状态
                if (!webAppVal && !isPwaModeVal && userinfoVal && userinfoVal.wapToPwaIsOpen === 1) {
                    isShowPwaUpdate.value = true;
                } else {
                    isShowPwaUpdate.value = false;
                }
            },
            { immediate: true }
        );
    }


    // 计算点击遮罩是否关闭PWA更新弹窗
    function getIsClosePwaUpdate(userinfo) {
        // 如果等于 0  弹窗是可以关闭的，等于1是不能关闭
        if (!userinfo.value) {
            return true;
        }
        // 如果userinfo.value?.wapToPwaIsMust是undefined 则默认点击可以关闭
        if (userinfo.value?.wapToPwaIsMust === undefined) {
            return true;
        }
        return userinfo.value?.wapToPwaIsMust === 0;
    }

    
    // 设置GoogleToApk监听
    const isShowGoogleToApk = ref(false);
    function setupGoogleToApkWatch(userinfo) {
        watch(
            [() => userinfo.value, () => google.value],
            ([userinfoVal, googleVal]) => {
                // 是Google 并且 要求开启谷歌马甲包转内部apk
                if (googleVal && userinfoVal && userinfoVal.toGoogleIsOpen === 1) {
                    isShowGoogleToApk.value = true;
                } else {
                    isShowGoogleToApk.value = false;
                }
            },
            { immediate: true }
        );
    }

    // 计算点击遮罩是否关闭GoogleToApk弹窗
    function getIsCloseGoogleToApk(userinfo) {
        // 如果等于 0  弹窗是可以关闭的，等于1是不能关闭
        if (!userinfo.value) {
            return true;
        }
        // 如果userinfo.value?.toGoogleIsMust是undefined 则默认点击可以关闭
        if (userinfo.value?.toGoogleIsMust === undefined) {
            return true;
        }
        return userinfo.value?.toGoogleIsMust === 0;
    }


    return {
        isPwaMode,
        webApp,
        getClientType,
        getUrlParam,
        getUrlParams,
        getCookie,
        handleRouteAndWebviewParams,
        isShowPwaUpdate,
        setupPwaUpdateWatch,
        setupGoogleToApkWatch,
        isShowGoogleToApk,
        getIsClosePwaUpdate,
        isPwaInstalled,
        isRecoveredFromUninstall,
        google,
        isGoogleMode,
        getIsCloseGoogleToApk,
    };
}
